<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, Tag, Company, Event } from '@/models/content'

const props = defineProps({
  id: {
    type: String,
    require: true,
  },
  title: {
    type: String,
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  pillsLimit: {
    type: Number,
    default: 2,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  company: {
    type: Object as PropType<Company>,
    require: true,
  },
  event: {
    type: Object as PropType<Event>,
    require: true,
  },
})

const limitedPills = computed(() => props.pills.slice(0, props.pillsLimit))
const coverPosition = useCoverPosition(props.coverUrl)

const { callToAction, isDownloading, downloadProgress } = useDownloadFile()
</script>

<template>
  <ArchiveCard
    :date-and-duration="publishedDate"
    :title="title"
    :pills="limitedPills"
    @click="callToAction.callback(id, 'presentation', fileName)"
  >
    <template #cover>
      <DownloadOverlay
        :is-downloading="isDownloading"
        :download-progress="downloadProgress"
        :label="callToAction.text"
      />

      <img
        :src="$cloudinary.resize(coverUrl, 'archive-card-thumbnail')"
        class="h-full min-w-full object-cover"
        :class="coverPosition"
        width="300"
        height="150"
        alt=""
      />

      <div class="absolute top-0 h-full w-full border-2 border-black/10" />

      <div class="absolute right-1 top-1 hidden sm:right-3 sm:top-3 sm:flex">
        <PresentationBadge />
      </div>
    </template>

    <template #icon-mobile>
      <PresentationBadge icon-gray />
    </template>

    <template #details>
      <div>
        <p class="text-s-700">
          <span class="mr-1 font-bold">Company/Institution:</span>
          <span v-if="company && company.name">{{ company.name }}</span>
        </p>
        <p class="text-s-700">
          <span class="mr-1 font-bold">Event:</span>
          <span v-if="event && event.name">{{ event.name }}</span>
        </p>
        <p class="text-s-700" data-testid="presentation-card-date">
          <span class="mr-1 font-bold">Date:</span>
          <span v-if="publishedDate">{{ publishedDate }}</span>
        </p>
      </div>
    </template>
  </ArchiveCard>
</template>
